import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

import {
  ICON_DEFS,
  IconDef,
  ReferenceFileKind,
  extensionFromFileName,
  fileKindFromExtension,
  iconFill,
  iconStroke,
} from '@app/shared/common/tiptap-editor/extensions/reference-token';

/**
 * `vf-attachment-icon` — the type icon for a file/folder attachment.
 *
 * Deliberately renders the SAME icon set the notes' inserted-file tokens use (`reference-token`'s
 * `ICON_DEFS`, imported rather than copied so the two can't drift): hosted brand SVGs for Office,
 * a red outline for pdf, an amber folder, grey outlines for images and everything else. Pair it with
 * a `#2563eb` underlined name and an attachment reads identically in a note and in a comment
 * (design `Trovve.dc.html:1688` — "Attachments: inline with the body text").
 *
 * Not to be confused with `vf-file-icon`, the Files-TAB browser icon, which is a different, sparser
 * map (lucide glyphs, no pdf). Nor with `attachmentIconHtml`, which builds an icon as a MARKUP STRING
 * for the email reply body — that one has to survive tiptap's schema and a mail client, so it can only
 * use `<img>`/emoji, never the inline SVG below.
 */
@Component({
  selector: 'vf-attachment-icon',
  template: `<img *ngIf="icon.src" class="vf-att-ico-img" [src]="icon.src" alt="" draggable="false" />
    <span *ngIf="icon.glyph" class="vf-att-ico-glyph" aria-hidden="true">{{ icon.glyph }}</span>
    <svg *ngIf="!icon.src && !icon.glyph" class="vf-att-ico-svg" viewBox="0 0 24 24"
      [attr.fill]="fill" [attr.stroke]="stroke"
      stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path *ngFor="let d of icon.paths" [attr.d]="d" />
    </svg>`,
  styles: [
    `
      :host {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 auto;
        line-height: 1;
      }
      .vf-att-ico-img,
      .vf-att-ico-svg {
        width: 1em;
        height: 1em;
        object-fit: contain;
      }
      /* An emoji has no box to size — it scales with font-size, which the host already sets. */
      .vf-att-ico-glyph {
        font-size: 1em;
        line-height: 1;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AttachmentIconComponent {
  /** Filename — its extension picks the icon. Ignored when `isFolder` is set. */
  @Input() set name(value: string | null | undefined) {
    this._name = value || '';
    this.recompute();
  }

  @Input() set isFolder(value: boolean) {
    this._isFolder = !!value;
    this.recompute();
  }

  icon: IconDef = ICON_DEFS.generic;
  /** Solid for a `filled` def (the folder), transparent-with-stroke for the outline icons. */
  fill = iconFill(ICON_DEFS.generic);
  stroke = iconStroke(ICON_DEFS.generic);

  private _name = '';
  private _isFolder = false;

  private recompute(): void {
    if (this._isFolder) {
      this.icon = ICON_DEFS.folder;
    } else {
      const kind: ReferenceFileKind = fileKindFromExtension(extensionFromFileName(this._name));
      this.icon = ICON_DEFS[kind] || ICON_DEFS.generic;
    }
    this.fill = iconFill(this.icon);
    this.stroke = iconStroke(this.icon);
  }
}
